import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Plus, Search, Trash2, Pencil, Printer, X } from 'lucide-react';
import { toast } from 'sonner';

const blankItem = () => ({
  product_name: '', structure: '', size: '', material_code: '', batch_number: '',
  quantity: '', unit: 'Nos', unit_price: '', total_amount: '', cgst: '', sgst_igst: ''
});

const DISPATCH_MODES = ['Road', 'Air', 'Transport'];
const PAYMENT_TERMS = ['30 days', '45 days', '60 days', 'Against Delivery', 'Advance'];
const DELIVERY_TERMS = ['Door Delivery', 'Go-down Delivery'];

const Dispatch = () => {
  const [dispatches, setDispatches] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const emptyForm = {
    invoice_number: '', invoice_date: '', customer_id: '', customer_name: '',
    dispatched_through: 'Road', destination: '', vehicle_number: '',
    sales_order_id: '', sales_order_number: '', customer_so_number: '', so_date: '',
    terms_of_payment: '', terms_of_delivery: '',
    items: [blankItem()], remarks: ''
  };
  const [formData, setFormData] = useState({ ...emptyForm });

  useEffect(() => { fetchData(); }, []);
  const fetchData = async () => {
    setLoading(true);
    try {
      const [dRes, cRes, oRes] = await Promise.all([
        api.get('/production/dispatches'), api.get('/purchase/customers'), api.get('/sales/orders')
      ]);
      setDispatches(dRes.data); setCustomers(cRes.data); setOrders(oRes.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const openCreate = () => { setEditing(null); setFormData({ ...emptyForm, items: [blankItem()] }); setShowModal(true); };

  const openEdit = (d) => {
    setEditing(d);
    setFormData({
      invoice_number: d.invoice_number || '', invoice_date: d.invoice_date || '',
      customer_id: d.customer_id || '', customer_name: d.customer_name || '',
      dispatched_through: d.dispatched_through || 'Road', destination: d.destination || '', vehicle_number: d.vehicle_number || '',
      sales_order_id: d.sales_order_id || '', sales_order_number: d.sales_order_number || '',
      customer_so_number: d.customer_so_number || '', so_date: d.so_date || '',
      terms_of_payment: d.terms_of_payment || '', terms_of_delivery: d.terms_of_delivery || '',
      items: (d.items && d.items.length) ? d.items.map(it => ({ ...blankItem(), ...it })) : [blankItem()],
      remarks: d.remarks || ''
    });
    setShowModal(true);
  };

  const onCustomerChange = (cid) => {
    const c = customers.find(x => x.id === cid);
    setFormData(prev => ({ ...prev, customer_id: cid, customer_name: c?.name || '' }));
  };
  const onOrderChange = (oid) => {
    const o = orders.find(x => x.id === oid);
    if (o) {
      setFormData(prev => ({
        ...prev, sales_order_id: oid, sales_order_number: o.order_number, customer_name: o.customer_name,
        items: (o.items || []).map(i => ({ ...blankItem(), product_name: i.name, material_code: i.sku, quantity: String(i.quantity), unit_price: String(i.unit_price || ''), total_amount: String((i.quantity || 0) * (i.unit_price || 0)) }))
      }));
    } else {
      setFormData(prev => ({ ...prev, sales_order_id: '', sales_order_number: '' }));
    }
  };

  const updateItem = (idx, field, val) => {
    setFormData(prev => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [field]: val };
      if (field === 'quantity' || field === 'unit_price') {
        const q = parseFloat(items[idx].quantity) || 0;
        const p = parseFloat(items[idx].unit_price) || 0;
        if (items[idx].quantity !== '' && items[idx].unit_price !== '') items[idx].total_amount = String(Number((q * p).toFixed(2)));
      }
      return { ...prev, items };
    });
  };
  const addItem = () => setFormData(prev => ({ ...prev, items: [...prev.items, blankItem()] }));
  const removeItem = (idx) => setFormData(prev => prev.items.length > 1 ? ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }) : prev);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/production/dispatches/${editing.id}`, formData);
        toast.success('Dispatch updated');
      } else {
        await api.post('/production/dispatches', formData);
        toast.success('Dispatch created');
      }
      setShowModal(false); setEditing(null); setFormData({ ...emptyForm }); fetchData();
    } catch (err) {
      const msg = err.response?.status === 403 ? 'You are not authorized to modify dispatches' : (err.response?.data?.detail || 'Failed');
      toast.error(msg);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this dispatch?')) return;
    try { await api.delete(`/production/dispatches/${id}`); fetchData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const updateStatus = async (id, status) => {
    try { await api.put(`/production/dispatches/${id}`, { status }); fetchData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const getStatusBadge = (s) => {
    const m = { dispatched: 'badge-info', in_transit: 'badge-warning', delivered: 'badge-success', cancelled: 'badge-danger' };
    return `badge ${m[s] || 'badge-info'}`;
  };

  const printDispatch = (d) => {
    const rows = (d.items || []).map(it => `
      <tr>
        <td>${it.product_name || ''}</td><td>${it.structure || ''}</td><td>${it.size || ''}</td>
        <td>${it.material_code || ''}</td><td>${it.batch_number || ''}</td>
        <td style="text-align:right">${it.quantity || ''} ${it.unit || ''}</td>
        <td style="text-align:right">${it.unit_price || ''}</td><td style="text-align:right">${it.total_amount || ''}</td>
        <td style="text-align:right">${it.cgst || ''}</td><td style="text-align:right">${it.sgst_igst || ''}</td>
      </tr>`).join('');
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>${d.dispatch_number}</title>
      <style>
        @page{size:A4 landscape;margin:12mm}
        body{font-family:Arial,sans-serif;font-size:11px;color:#111;padding:14px}
        h1{font-size:18px;margin:0 0 6px}
        .meta{display:grid;grid-template-columns:1fr 1fr 1fr;gap:3px 20px;margin:10px 0;font-size:11px}
        table{width:100%;border-collapse:collapse;margin-top:8px}
        th,td{border:1px solid #666;padding:5px 6px;text-align:left;font-size:10px}
        th{background:#1a237e;color:#fff}
        @media print{button{display:none !important}}
      </style></head><body>
      <h1>Dispatch Note</h1>
      <div class="meta">
        <div><strong>Dispatch No:</strong> ${d.dispatch_number}</div>
        <div><strong>Invoice No:</strong> ${d.invoice_number || '-'}</div>
        <div><strong>Invoice Date:</strong> ${d.invoice_date || '-'}</div>
        <div><strong>Customer:</strong> ${d.customer_name || '-'}</div>
        <div><strong>Dispatched Through:</strong> ${d.dispatched_through || '-'}</div>
        <div><strong>Destination:</strong> ${d.destination || '-'}</div>
        <div><strong>Vehicle No:</strong> ${d.vehicle_number || '-'}</div>
        <div><strong>Customer SO No:</strong> ${d.customer_so_number || '-'}</div>
        <div><strong>SO Date:</strong> ${d.so_date || '-'}</div>
        <div><strong>Terms of Payment:</strong> ${d.terms_of_payment || '-'}</div>
        <div><strong>Terms of Delivery:</strong> ${d.terms_of_delivery || '-'}</div>
      </div>
      <table><thead><tr>
        <th>Product Name</th><th>Structure</th><th>Size</th><th>Material Code</th><th>Batch No</th>
        <th>Quantity</th><th>Unit Price</th><th>Total Amount</th><th>CGST</th><th>SGST/IGST</th>
      </tr></thead><tbody>${rows}</tbody></table>
      ${d.remarks ? `<div style="margin-top:10px"><strong>Remarks:</strong> ${d.remarks}</div>` : ''}
      <button onclick="window.print()" style="margin-top:16px;padding:8px 24px;cursor:pointer">Print</button>
      </body></html>`);
    w.document.close();
  };

  const filtered = dispatches.filter(d =>
    (d.dispatch_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (d.customer_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (d.invoice_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (d.vehicle_number || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div data-testid="dispatch-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Dispatch</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text-tertiary)', fontSize: '13px' }}>Manage outbound shipments and delivery tracking</p>
        </div>
        <button onClick={openCreate} className="btn btn-primary" data-testid="create-dispatch-btn"><Plus size={16} /> New Dispatch</button>
      </div>

      <div className="grid grid-cols-4 mb-4">
        {['dispatched', 'in_transit', 'delivered', 'cancelled'].map(st => (
          <div key={st} className="stat-card" data-testid={`stat-${st}`}>
            <div className="stat-value">{dispatches.filter(d => d.status === st).length}</div>
            <div className="stat-label" style={{ textTransform: 'capitalize' }}>{st.replace('_', ' ')}</div>
          </div>
        ))}
      </div>

      <div className="card mb-4">
        <div className="form-group" style={{ marginBottom: 0, position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
          <input type="text" className="form-input" placeholder="Search dispatch #, customer, invoice, vehicle..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ paddingLeft: '40px' }} data-testid="search-input" />
        </div>
      </div>

      <div className="card">
        {loading ? <div>Loading...</div> : (
          <div className="table-container">
            <table>
              <thead><tr><th>Dispatch #</th><th>Invoice #</th><th>Customer</th><th>Destination</th><th>Vehicle</th><th>Through</th><th>Date</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {filtered.length === 0 ? <tr><td colSpan={9} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-tertiary)' }}>No dispatches</td></tr> : filtered.map(d => (
                  <tr key={d.id} data-testid={`dispatch-row-${d.dispatch_number}`}>
                    <td className="font-mono">{d.dispatch_number}</td>
                    <td className="font-mono">{d.invoice_number || '-'}</td>
                    <td>{d.customer_name || '-'}</td>
                    <td>{d.destination || '-'}</td>
                    <td>{d.vehicle_number || '-'}</td>
                    <td>{d.dispatched_through || '-'}</td>
                    <td className="text-sm">{d.invoice_date || d.dispatch_date || '-'}</td>
                    <td><span className={getStatusBadge(d.status)}>{(d.status || '').replace('_', ' ')}</span></td>
                    <td>
                      <div className="flex gap-1">
                        <button onClick={() => openEdit(d)} className="btn btn-ghost btn-sm" title="Edit"><Pencil size={14} /></button>
                        <button onClick={() => printDispatch(d)} className="btn btn-ghost btn-sm" title="Print"><Printer size={14} /></button>
                        {d.status === 'dispatched' && <button onClick={() => updateStatus(d.id, 'in_transit')} className="btn btn-secondary btn-sm" style={{ fontSize: '11px' }}>In Transit</button>}
                        {d.status === 'in_transit' && <button onClick={() => updateStatus(d.id, 'delivered')} className="btn btn-secondary btn-sm" style={{ fontSize: '11px' }}>Delivered</button>}
                        <button onClick={() => handleDelete(d.id)} className="btn btn-ghost btn-sm" title="Delete"><Trash2 size={14} color="#dc2626" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" data-testid="dispatch-modal">
          <div className="modal-content" style={{ maxWidth: '1000px', maxHeight: '90vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>{editing ? `Edit ${editing.dispatch_number}` : 'Create Dispatch'}</h2></div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="grid grid-cols-3" style={{ gap: '12px' }}>
                  <div className="form-group"><label className="form-label">Invoice No. *</label><input type="text" className="form-input" value={formData.invoice_number} onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })} required data-testid="invoice-input" /></div>
                  <div className="form-group"><label className="form-label">Invoice Date</label><input type="date" className="form-input" value={formData.invoice_date} onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })} data-testid="invoice-date" /></div>
                  <div className="form-group">
                    <label className="form-label">Customer</label>
                    <select className="form-select" value={formData.customer_id} onChange={(e) => onCustomerChange(e.target.value)} data-testid="customer-select">
                      <option value="">Select...</option>
                      {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Dispatched Through</label>
                    <select className="form-select" value={formData.dispatched_through} onChange={(e) => setFormData({ ...formData, dispatched_through: e.target.value })} data-testid="through-select">
                      {DISPATCH_MODES.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label className="form-label">Destination</label><input type="text" className="form-input" value={formData.destination} onChange={(e) => setFormData({ ...formData, destination: e.target.value })} data-testid="destination-input" /></div>
                  <div className="form-group"><label className="form-label">Vehicle No.</label><input type="text" className="form-input" value={formData.vehicle_number} onChange={(e) => setFormData({ ...formData, vehicle_number: e.target.value })} data-testid="vehicle-input" /></div>
                  <div className="form-group">
                    <label className="form-label">Sales Order (internal)</label>
                    <select className="form-select" value={formData.sales_order_id} onChange={(e) => onOrderChange(e.target.value)} data-testid="order-select">
                      <option value="">Select SO...</option>
                      {orders.map(o => <option key={o.id} value={o.id}>{o.order_number} - {o.customer_name}</option>)}
                    </select>
                  </div>
                  <div className="form-group"><label className="form-label">Customer Sales Order No.</label><input type="text" className="form-input" value={formData.customer_so_number} onChange={(e) => setFormData({ ...formData, customer_so_number: e.target.value })} data-testid="cust-so-input" /></div>
                  <div className="form-group"><label className="form-label">Sales Order Date</label><input type="date" className="form-input" value={formData.so_date} onChange={(e) => setFormData({ ...formData, so_date: e.target.value })} data-testid="so-date-input" /></div>
                  <div className="form-group">
                    <label className="form-label">Terms of Payment</label>
                    <select className="form-select" value={formData.terms_of_payment} onChange={(e) => setFormData({ ...formData, terms_of_payment: e.target.value })} data-testid="pay-terms-select">
                      <option value="">Select...</option>
                      {PAYMENT_TERMS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Terms of Delivery</label>
                    <select className="form-select" value={formData.terms_of_delivery} onChange={(e) => setFormData({ ...formData, terms_of_delivery: e.target.value })} data-testid="del-terms-select">
                      <option value="">Select...</option>
                      {DELIVERY_TERMS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', margin: '16px 0 8px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: 700, margin: 0 }}>Product Details</h3>
                  <button type="button" onClick={addItem} className="btn btn-secondary btn-sm"><Plus size={12} /> Add Product</button>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', fontSize: '12px' }}>
                    <thead><tr>
                      <th style={{ padding: 4 }}>Product Name</th><th style={{ padding: 4 }}>Structure</th><th style={{ padding: 4 }}>Size</th>
                      <th style={{ padding: 4 }}>Material Code</th><th style={{ padding: 4 }}>Batch No</th>
                      <th style={{ padding: 4 }}>Qty</th><th style={{ padding: 4 }}>Unit</th>
                      <th style={{ padding: 4 }}>Unit Price</th><th style={{ padding: 4 }}>Total</th>
                      <th style={{ padding: 4 }}>CGST</th><th style={{ padding: 4 }}>SGST/IGST</th><th style={{ width: 28 }}></th>
                    </tr></thead>
                    <tbody>
                      {formData.items.map((item, idx) => (
                        <tr key={idx}>
                          <td style={{ padding: 3 }}><input type="text" className="form-input" value={item.product_name} onChange={(e) => updateItem(idx, 'product_name', e.target.value)} style={{ fontSize: 12, padding: 4, minWidth: 110 }} /></td>
                          <td style={{ padding: 3 }}><input type="text" className="form-input" value={item.structure} onChange={(e) => updateItem(idx, 'structure', e.target.value)} style={{ fontSize: 12, padding: 4, width: 90 }} /></td>
                          <td style={{ padding: 3 }}><input type="text" className="form-input" value={item.size} onChange={(e) => updateItem(idx, 'size', e.target.value)} style={{ fontSize: 12, padding: 4, width: 70 }} /></td>
                          <td style={{ padding: 3 }}><input type="text" className="form-input" value={item.material_code} onChange={(e) => updateItem(idx, 'material_code', e.target.value)} style={{ fontSize: 12, padding: 4, width: 90 }} /></td>
                          <td style={{ padding: 3 }}><input type="text" className="form-input" value={item.batch_number} onChange={(e) => updateItem(idx, 'batch_number', e.target.value)} style={{ fontSize: 12, padding: 4, width: 80 }} /></td>
                          <td style={{ padding: 3 }}><input type="number" className="form-input" value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', e.target.value)} style={{ fontSize: 12, padding: 4, width: 60 }} /></td>
                          <td style={{ padding: 3 }}>
                            <select className="form-select" value={item.unit} onChange={(e) => updateItem(idx, 'unit', e.target.value)} style={{ fontSize: 12, padding: 4, width: 64 }}>
                              <option value="Nos">Nos</option><option value="Kgs">Kgs</option>
                            </select>
                          </td>
                          <td style={{ padding: 3 }}><input type="number" step="0.01" className="form-input" value={item.unit_price} onChange={(e) => updateItem(idx, 'unit_price', e.target.value)} style={{ fontSize: 12, padding: 4, width: 80 }} /></td>
                          <td style={{ padding: 3 }}><input type="number" step="0.01" className="form-input" value={item.total_amount} onChange={(e) => updateItem(idx, 'total_amount', e.target.value)} style={{ fontSize: 12, padding: 4, width: 90 }} /></td>
                          <td style={{ padding: 3 }}><input type="number" step="0.01" className="form-input" value={item.cgst} onChange={(e) => updateItem(idx, 'cgst', e.target.value)} style={{ fontSize: 12, padding: 4, width: 70 }} /></td>
                          <td style={{ padding: 3 }}><input type="number" step="0.01" className="form-input" value={item.sgst_igst} onChange={(e) => updateItem(idx, 'sgst_igst', e.target.value)} style={{ fontSize: 12, padding: 4, width: 80 }} /></td>
                          <td style={{ padding: 3 }}>{formData.items.length > 1 && <button type="button" onClick={() => removeItem(idx)} className="btn btn-ghost btn-sm"><X size={14} color="#DC2626" /></button>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="form-group" style={{ marginTop: '12px' }}><label className="form-label">Remarks</label><textarea className="form-input" value={formData.remarks} onChange={(e) => setFormData({ ...formData, remarks: e.target.value })} rows={2} data-testid="remarks" /></div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => { setShowModal(false); setEditing(null); }} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-dispatch-btn">{editing ? 'Update Dispatch' : 'Create Dispatch'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dispatch;
